import React from "react";
import { Bell, Layers, MoreHorizontal, Save, Settings2, Shield, User, Users } from "lucide-react";
import { toast } from "sonner";

import { AppLayout } from "@/components/layout/AppLayout";
import { useTranslation } from "@/lib/i18n";
import { useAuthEmail, useAuthFullName, useUpdateProfile, useUserRole, useUserWorkspaces } from "@/lib/auth-store";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export default function SettingsPage() {
  const { t } = useTranslation();
  const fullName = useAuthFullName();
  const email = useAuthEmail();
  const workspaces = useUserWorkspaces();
  const role = useUserRole();
  const updateProfile = useUpdateProfile();

  const [draftFullName, setDraftFullName] = React.useState(fullName || "");

  React.useEffect(() => {
    setDraftFullName(fullName || "");
  }, [fullName]);

  const handleSave = () => {
    updateProfile({ fullName: draftFullName.trim() || fullName || "" });
    toast.success("Đã lưu thay đổi hồ sơ.");
  };

  return (
    <AppLayout container>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Cài đặt hệ thống</h1>
          <p className="mt-1 text-muted-foreground">Quản lý hồ sơ cá nhân, workspace, bảo mật và các tùy chọn vận hành.</p>
        </div>

        <Tabs defaultValue="profile" className="space-y-4">
          <TabsList className="flex h-auto flex-wrap bg-muted/50 p-1">
            <TabsTrigger value="profile" className="gap-2">
              <User className="h-4 w-4" /> Hồ sơ
            </TabsTrigger>
            <TabsTrigger value="workspaces" className="gap-2">
              <Layers className="h-4 w-4" /> {t("settings_tab_workspaces")}
            </TabsTrigger>
            <TabsTrigger value="team" className="gap-2">
              <Users className="h-4 w-4" /> {t("settings_tab_team")}
            </TabsTrigger>
            <TabsTrigger value="security" className="gap-2">
              <Shield className="h-4 w-4" /> Bảo mật
            </TabsTrigger>
            <TabsTrigger value="notifications" className="gap-2">
              <Bell className="h-4 w-4" /> Thông báo
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4">
            <Card className="border-white/5">
              <CardHeader>
                <CardTitle>Thông tin cá nhân</CardTitle>
                <CardDescription>Cập nhật thông tin tài khoản chính của bạn tại đây.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Họ và tên</Label>
                    <Input id="fullName" value={draftFullName} onChange={(event) => setDraftFullName(event.target.value)} placeholder="Nguyen Van A" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Địa chỉ email</Label>
                    <Input id="email" defaultValue={email || ""} disabled />
                  </div>
                </div>
                <Button onClick={handleSave} className="gap-2">
                  <Save className="h-4 w-4" /> Lưu thay đổi
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="workspaces" className="space-y-4">
            <Card className="border-white/5">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t("settings_tab_workspaces")}</CardTitle>
                  <CardDescription>Quản lý phân tách môi trường và nhóm tài nguyên theo nghiệp vụ.</CardDescription>
                </div>
                <Button size="sm">Tạo workspace</Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tên</TableHead>
                      <TableHead>Loại</TableHead>
                      <TableHead>Trạng thái</TableHead>
                      <TableHead className="text-right">Tác vụ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {workspaces.map((workspace) => (
                      <TableRow key={workspace.id}>
                        <TableCell className="font-medium">{workspace.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{workspace.type}</Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className="border-green-500/20 bg-green-500/10 text-green-500">Active</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon">
                            <Settings2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="team" className="space-y-4">
            <Card className="border-white/5">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>{t("settings_tab_team")}</CardTitle>
                  <CardDescription>Quản lý vai trò và quyền truy cập của người dùng trong tổ chức.</CardDescription>
                </div>
                <Button size="sm">Mời thành viên</Button>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Người dùng</TableHead>
                      <TableHead>Vai trò</TableHead>
                      <TableHead>Trạng thái</TableHead>
                      <TableHead className="text-right">Tác vụ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell>
                        <div className="flex flex-col">
                          <span className="font-medium">{fullName}</span>
                          <span className="text-xs text-muted-foreground">{email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{role}</Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-green-500">
                          Active
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security" className="space-y-4">
            <Card className="border-white/5">
              <CardHeader>
                <CardTitle>Cấu hình bảo mật</CardTitle>
                <CardDescription>Quản lý xác thực hai lớp và mức độ an toàn của phiên làm việc.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between rounded-lg border bg-secondary/20 p-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Xác thực hai lớp (2FA)</p>
                    <p className="text-xs text-muted-foreground">Thêm một lớp bảo vệ cho tài khoản quản trị và truy cập nhạy cảm.</p>
                  </div>
                  <Switch />
                </div>
                <Button variant="outline" className="text-destructive hover:bg-destructive/10">
                  Thu hồi toàn bộ phiên đang hoạt động
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="space-y-4">
            <Card className="border-white/5">
              <CardHeader>
                <CardTitle>Tùy chọn thông báo</CardTitle>
                <CardDescription>Chọn cách bạn muốn nhận các cảnh báo và báo cáo vận hành.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {[
                  { label: "Cảnh báo nghiêm trọng", desc: "Sự cố bảo mật và lỗi hệ thống cần xử lý ngay." },
                  { label: "Báo cáo sử dụng", desc: "Tóm tắt hoạt động và chi phí theo chu kỳ." },
                ].map((item) => (
                  <div key={item.label} className="flex items-center justify-between border-b py-2 last:border-0">
                    <div className="space-y-0.5">
                      <p className="text-sm font-medium">{item.label}</p>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                    <Switch defaultChecked />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
